import { QueryClient, VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query';

// Defaults chosen to mirror the previous hand-rolled cache stores, which loaded
// each list once and held it for the whole session:
// - gcTime Infinity: keep cached data even when no component is observing it. This
//   is the important one — the old Pinia stores held their arrays for the session,
//   so navigating away and back always showed data instantly. The default 5m gcTime
//   evicted the cache once a page's components unmounted, causing a visible reload
//   (empty list + spinner) on return; Infinity prevents that. These are small
//   reference lists, so holding them costs little.
// - staleTime 5m: after the window, the next visit refreshes the data in the
//   background. Because cached data is already on screen (gcTime Infinity), this is
//   silent — no loading state — so users still get periodic freshness without a
//   visible reload. Mutations refresh immediately via the explicit `refetch*` helpers.
// - retry false: axios interceptors already surface HTTP errors and a retry would
//   delay the session-expired flow on a 401/419.
// - refetchOnWindowFocus false: warehouse staff alt-tab constantly; opt into it
//   per-query where it's actually wanted.
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000,
            gcTime: Infinity,
            retry: false,
            refetchOnWindowFocus: false,
        },
    },
});

export const vueQueryPluginOptions: VueQueryPluginOptions = { queryClient };

// Exported so module-level code that runs outside a component setup can reach the same
// cache the components observe — `useQueryClient()` only works inside setup. Used by the
// query composables for imperative ops from standalone functions and plain `.ts` item
// managers: `ensureQueryData`/`invalidateQueries`, and the synchronous `getQueryData` reads
// in `useItemGroupConfigurations`. This is the same instance provided to
// `app.use(VueQueryPlugin, ...)`, so those ops hit the cache every `useQuery` observer reads from.
export { queryClient, VueQueryPlugin };
